/**
 * Gestor de escritórios: console de quem administra a PLATAFORMA (usuário super administrador), não um escritório.
 * Cria escritórios novos com o primeiro administrador, acompanha o uso, define limites do plano e suspende/reativa.
 * Não dá acesso aos dados de dentro de cada escritório — só aos números.
 */
import { z } from 'zod';
import { DataISO, Id, limparDocumento } from './comum.js';

const Limite = z.number().int().min(1).max(100_000).nullable();
const Cnpj = z.string().transform(limparDocumento).refine((d) => d === '' || d.length === 14, 'CNPJ deve ter 14 dígitos').transform((d) => d || null);

export const EscritorioPlataforma = z.object({
  id: Id,
  nome: z.string(),
  cnpj: z.string().nullable(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  suspensoEm: z.string().nullable(),
  suspensoMotivo: z.string().nullable(),
  /** limites do plano; null = sem limite */
  limiteClientes: z.number().int().nullable(),
  limiteMembros: z.number().int().nullable(),
  observacao: z.string().nullable(),
  /** período de teste até esta data (AAAA-MM-DD); null = cliente efetivo */
  testeAte: z.string().nullable(),
  testeVencido: z.boolean(),
  membrosAtivos: z.number().int(),
  /** clientes não baixados — é o que o limite conta */
  clientesAtivos: z.number().int(),
  tarefasAbertas: z.number().int(),
  /** último uso de qualquer membro com este escritório ativo na sessão */
  ultimoAcessoEm: z.string().nullable(),
  administradores: z.array(z.object({ nome: z.string(), email: z.string(), ativo: z.boolean(), duasEtapas: z.boolean() })),
  /** é o escritório em que eu estou agora (não pode ser suspenso por aqui) */
  meu: z.boolean(),
});
export type EscritorioPlataforma = z.infer<typeof EscritorioPlataforma>;

export const EscritorioPlataformaBody = z.object({
  nome: z.string().trim().min(2).max(120),
  cnpj: Cnpj.optional(),
  adminNome: z.string().trim().min(2).max(120),
  adminEmail: z.email().max(200),
  limiteClientes: Limite.optional(),
  limiteMembros: Limite.optional(),
  observacao: z.string().trim().max(500).nullable().optional(),
  testeAte: DataISO.nullable().optional(),
});
export type EscritorioPlataformaBody = z.infer<typeof EscritorioPlataformaBody>;

/** PATCH: só o que vier muda (sem padrões). */
export const EscritorioPlataformaEditarBody = z.object({
  nome: z.string().trim().min(2).max(120).optional(),
  cnpj: Cnpj.optional(),
  limiteClientes: Limite.optional(),
  limiteMembros: Limite.optional(),
  observacao: z.string().trim().max(500).nullable().optional(),
  testeAte: DataISO.nullable().optional(),
});
export type EscritorioPlataformaEditarBody = z.infer<typeof EscritorioPlataformaEditarBody>;

export const EscritorioSuspenderBody = z.object({ motivo: z.string().trim().min(3).max(300) });
export type EscritorioSuspenderBody = z.infer<typeof EscritorioSuspenderBody>;

export const EscritorioCriado = z.object({
  escritorio: EscritorioPlataforma,
  /** true = pessoa nova, recebeu convite para definir a senha; false = já tinha conta, o escritório aparece no seletor dela */
  conviteEnviado: z.boolean(),
});
export type EscritorioCriado = z.infer<typeof EscritorioCriado>;
